import {
  toggleArchiveIngredientPath,
  toggleArchiveInventoryPath,
  bulkUpdateArchivedIngredientsPath,
} from "routes";
import { fetchScript } from "~/recipal-app/controllers/shared/requests";
import { Flash } from "~/jose_modern/flash";
import { Modal } from "~/jose_modern/modal";
import { hideAll as hideAllTippys } from "tippy.js";

const MODAL_ID = "archive-ingredient-modal";
const SELECTOR = `#${MODAL_ID}`;

let state = {
  context: "ingredient", // "ingredient" | "inventory"
  ingredientId: null,
  inventoryId: null,
  ingredientName: "",
  isArchived: false,
  // Forward-compat for the cascade branch; ignored here.
  inventoryOnly: false,
  userId: null,
  bulkIds: [],
  bulkCount: 0,
  isSubmitting: false,
};

const ACTION_CONFIG = {
  archive: { verb: "Archive", buttonClass: "button is-warning" },
  restore: { verb: "Restore", buttonClass: "button is-success" },
};

const TARGETS = {
  ingredient: {
    title: "Ingredient",
    targetText: "",
    bodyMessages: {
      archive:
        "Archived ingredients are hidden from search results and your ingredient dashboard. Recipes that already use them are unaffected.",
      restore: "Restored ingredients return to search results and your ingredient dashboard.",
    },
  },
  inventory: {
    title: "Raw Material",
    targetText: "raw material for ",
    bodyMessages: {
      archive:
        "Archived raw materials are hidden by default. Use the filter to find them and unarchive at any time.",
      restore: "Restored raw materials return to the default inventory view.",
    },
  },
};

function isBulkIngredient() {
  return state.context === "ingredient" && state.bulkIds.length > 0;
}

function updateContent() {
  const action = state.isArchived ? "restore" : "archive";
  const actionConfig = ACTION_CONFIG[action];
  const target = TARGETS[state.context];
  const bulk = isBulkIngredient();
  const modal = document.getElementById(MODAL_ID);

  const displayName = bulk ? `${state.bulkCount} ingredients` : state.ingredientName;
  const titleNoun = `${target.title}${bulk ? "s" : ""}`;

  modal.querySelector("[data-title]").textContent = `Confirm ${actionConfig.verb} ${titleNoun}`;
  modal.querySelector("[data-action-word]").textContent = action;
  modal.querySelector("[data-target-text]").textContent = target.targetText;
  modal.querySelector("[data-name]").textContent = displayName;
  modal.querySelector("[data-context]").textContent = target.bodyMessages[action];

  const btn = modal.querySelector("[data-confirm]");
  btn.textContent = actionConfig.verb;
  btn.className = actionConfig.buttonClass;
  btn.disabled = false;
}

export function openArchiveModal(data) {
  if (!document.getElementById(MODAL_ID)) return;

  state = {
    context: data.context || "ingredient",
    ingredientId: data.ingredientId || null,
    inventoryId: data.inventoryId || null,
    ingredientName: data.ingredientName || "this ingredient",
    isArchived: data.isArchived || false,
    inventoryOnly: data.inventoryOnly || false,
    userId: data.userId || null,
    bulkIds: data.bulkIds || [],
    bulkCount: data.bulkCount || (data.bulkIds || []).length,
    isSubmitting: false,
  };

  updateContent();
  hideAllTippys();
  Modal.show(SELECTOR);
}

function setSubmitting() {
  state.isSubmitting = true;
  const btn = document.querySelector(`${SELECTOR} [data-confirm]`);
  btn.textContent = "Processing...";
  btn.disabled = true;
}

async function handleConfirm() {
  if (state.isSubmitting) return;
  setSubmitting();

  try {
    if (isBulkIngredient()) {
      await submitBulk();
    } else if (state.context === "inventory") {
      await submitInventoryToggle();
    } else {
      await submitIngredientToggle();
    }
  } catch (error) {
    console.error("Archive action failed:", error);
    Flash.error("An unexpected error occurred");
    Modal.hide(SELECTOR);
  }
}

// Inventory toggle: backend returns a .js.erb response that replaces the
// archive toggle button partial inline. Close the modal optimistically and
// let fetchScript execute the response.
async function submitInventoryToggle() {
  const url = toggleArchiveInventoryPath(state.inventoryId);
  Modal.hide(SELECTOR);

  const params = new URLSearchParams(window.location.search);
  if (state.inventoryOnly) {
    params.set("inventory_only", "1");
  }
  await fetchScript(`${url}?${params.toString()}`, { method: "PUT" });
}

// Ingredient archive/restore: backend returns JSON. Dispatch a DOM event on
// success so the dashboard can react (refresh its list).
async function submitIngredientToggle() {
  const url = toggleArchiveIngredientPath(state.ingredientId);
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify({ archived: !state.isArchived }),
  });

  if (response.ok) {
    Modal.hide(SELECTOR);
    const data = await response.json();
    dispatchModalEvent(data.archived ? "archived" : "restored", {
      ingredientId: state.ingredientId,
    });
  } else {
    const data = await response.json().catch(() => ({ alert: "Unknown error" }));
    Flash.error(data.alert || "Failed to update ingredient");
    Modal.hide(SELECTOR);
  }
}

async function submitBulk() {
  const url = bulkUpdateArchivedIngredientsPath();

  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify({
      user_id: state.userId,
      bulk_update_ingredients_archived: {
        ingredient_ids: state.bulkIds,
        archived: !state.isArchived,
      },
    }),
  });

  if (response.ok) {
    Modal.hide(SELECTOR);
    const data = await response.json().catch(() => ({}));
    if (data.notice) Flash[data.flash_type || "success"](data.notice);
    dispatchModalEvent(state.isArchived ? "restored" : "archived", {
      affectedIds: data.affected_ids || [],
    });
  } else {
    const data = await response.json().catch(() => ({}));
    Flash.error(data.error || data.alert || "Failed to update ingredients");
    Modal.hide(SELECTOR);
  }
}

function dispatchModalEvent(eventName, detail) {
  const modal = document.getElementById(MODAL_ID);
  if (modal) {
    modal.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
  }
}

document.addEventListener("click", (event) => {
  if (event.target.closest(`${SELECTOR} [data-confirm]`)) {
    handleConfirm();
  }
});

document.addEventListener("open-archive-modal", (event) => {
  openArchiveModal(event.detail);
});

window.openArchiveModalFromInventory = function (element) {
  openArchiveModal({
    context: "inventory",
    inventoryId: parseInt(element.dataset.inventoryId),
    ingredientName: element.dataset.ingredientName,
    isArchived: element.dataset.archived === "true",
    inventoryOnly: element.dataset.inventoryOnly === "true",
  });
};

window.openArchiveModalFromIngredient = function (element) {
  openArchiveModal({
    context: "ingredient",
    ingredientId: parseInt(element.dataset.ingredientId),
    ingredientName: element.dataset.ingredientName,
    isArchived: element.dataset.archived === "true",
  });
};
